import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, ArrowLeft, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError, prefixUrl } from "@/lib/api";

function normalizePublicPost(value) {
  if (!value || typeof value !== "object") return value;
  let tags = value.tags;
  if (!Array.isArray(tags) && typeof value.tags_json === "string") {
    try {
      tags = JSON.parse(value.tags_json);
    } catch {
      tags = [];
    }
  }
  return {
    ...value,
    cover_image: prefixUrl(value.cover_image),
    tags: Array.isArray(tags) ? tags.filter((tag) => typeof tag === "string" && tag.trim()) : [],
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/blog/${slug}`)
      .then((res) => setPost(normalizePublicPost(res.data)))
      .catch((requestError) => {
        setPost(null);
        setError(requestError?.response?.status === 404 ? "not_found" : (formatApiError(requestError) || "The post could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [slug, reloadKey]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 animate-pulse">
        <div className="h-6 bg-muted rounded w-3/4 mb-4" />
        <div className="h-4 bg-muted rounded w-1/2 mb-8" />
        <div className="space-y-3">
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-full" />
          <div className="h-3 bg-muted rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (!post && error && error !== "not_found") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" role="alert">
        <h1 className="text-2xl font-bold mb-3">Post could not be loaded</h1>
        <p className="text-muted-foreground">{error}</p>
        <Button type="button" className="mt-6" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="h-4 w-4 mr-2" />Try again</Button>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-primary hover:underline">← Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to Blog
      </Link>

      {post.category && <Badge variant="secondary" className="mb-3">{post.category}</Badge>}
      <h1 className="text-3xl font-bold mb-3">{post.title}</h1>
      
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
        {post.author && (
          <span className="flex items-center gap-1">
            <User className="h-4 w-4" /> {post.author}
          </span>
        )}
        {post.created_at && (
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {new Date(post.created_at).toLocaleDateString("en-IN", {
              year: "numeric", month: "long", day: "numeric"
            })}
          </span>
        )}
      </div>

      {post.cover_image && (
        <img src={post.cover_image} alt={post.title} className="w-full rounded-xl mb-8 max-h-96 object-cover" />
      )}

      <div className="prose prose-lg max-w-none">
        {post.content?.split("\n").map((paragraph, i) => (
          <p key={i} className="text-muted-foreground mb-4">{paragraph}</p>
        ))}
      </div>

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
          {post.tags.map((tag) => (
            <Badge key={tag} variant="outline">{tag}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}
